import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { usePPCAuth, PPC_API_URL } from './PPCContext';

const UserManagement = () => {
  const { user } = usePPCAuth();
  const navigate = useNavigate();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [toast, setToast] = useState(null);
  
  // Create user form
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    full_name: '',
    email: '',
    role: 'user'
  });
  
  // Reset password form
  const [newPassword, setNewPassword] = useState('');

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${PPC_API_URL}/auth.php?action=get-users`, getAuthHeader());
      if (res.data.success) {
        setUsers(res.data.users);
      }
    } catch (err) {
      showToast('Failed to fetch users', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/ppc');
      return;
    }
    fetchUsers();
  }, [user, navigate, fetchUsers]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${PPC_API_URL}/auth.php?action=create-user`, newUser, getAuthHeader());
      if (res.data.success) {
        showToast('User created successfully!');
        setShowCreateModal(false);
        setNewUser({ username: '', password: '', full_name: '', email: '', role: 'user' });
        fetchUsers();
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to create user', 'error');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.put(`${PPC_API_URL}/auth.php?action=update-user`, selectedUser, getAuthHeader());
      if (res.data.success) {
        showToast('User updated successfully!');
        setShowEditModal(false);
        fetchUsers();
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to update user', 'error');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${PPC_API_URL}/auth.php?action=reset-password`, {
        id: selectedUser.id,
        password: newPassword
      }, getAuthHeader());
      if (res.data.success) {
        showToast('Password reset successfully!');
        setShowResetModal(false);
        setNewPassword('');
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to reset password', 'error');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      const res = await axios.delete(`${PPC_API_URL}/auth.php?action=delete-user&id=${userId}`, getAuthHeader());
      if (res.data.success) {
        showToast('User deleted successfully!');
        fetchUsers();
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to delete user', 'error');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: { background: 'linear-gradient(135deg, #dc2626, #ef4444)', icon: '👑' },
      user: { background: 'linear-gradient(135deg, #3b82f6, #60a5fa)', icon: '👤' },
      viewer: { background: 'linear-gradient(135deg, #6b7280, #9ca3af)', icon: '👁️' }
    };
    const style = styles[role] || styles.user;
    return (
      <span style={{
        padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '600',
        background: style.background, color: 'white', textTransform: 'uppercase'
      }}>
        {style.icon} {role}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: '#10b981',
      inactive: '#f59e0b',
      locked: '#ef4444'
    };
    return (
      <span style={{
        padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
        background: `${colors[status]}20`, color: colors[status], textTransform: 'uppercase'
      }}>
        {status}
      </span>
    );
  };

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/ppc')}>← Back</button>
          <h1 className="dashboard-title">USER MANAGEMENT</h1>
        </div>
        <div className="header-right">
          <button onClick={() => setShowCreateModal(true)} style={{
            padding: '10px 20px', border: '1px solid rgba(255,255,255,.28)', borderRadius: '8px',
            background: 'rgba(255,255,255,.16)', color: 'white',
            cursor: 'pointer', fontSize: '14px', fontWeight: '600'
          }}>+ Create User</button>
        </div>
      </header>

      <main className="dashboard-content" style={{ padding: '20px' }}>
        {/* Stats Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div style={{ background: 'linear-gradient(135deg, #d32f2f 0%, #1565c0 100%)', color: 'white', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
            <div style={{ fontSize: '12px', opacity: 0.9 }}>Total Users</div>
            <div style={{ fontSize: '32px', fontWeight: '700' }}>{users.length}</div>
          </div>
          <div style={{ background: 'linear-gradient(135deg, #2e7d32, #388e3c)', color: 'white', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
            <div style={{ fontSize: '12px', opacity: 0.9 }}>Active</div>
            <div style={{ fontSize: '32px', fontWeight: '700' }}>{users.filter(u => u.status === 'active').length}</div>
          </div>
          <div style={{ background: 'linear-gradient(135deg, #c62828, #e53935)', color: 'white', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
            <div style={{ fontSize: '12px', opacity: 0.9 }}>Admins</div>
            <div style={{ fontSize: '32px', fontWeight: '700' }}>{users.filter(u => u.role === 'admin').length}</div>
          </div>
          <div style={{ background: 'linear-gradient(135deg, #1565c0, #1976d2)', color: 'white', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
            <div style={{ fontSize: '12px', opacity: 0.9 }}>Viewers</div>
            <div style={{ fontSize: '32px', fontWeight: '700' }}>{users.filter(u => u.role === 'viewer').length}</div>
          </div>
        </div>

        {/* Users Table */}
        <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Username</th>
                  <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Full Name</th>
                  <th style={{ padding: '14px', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Role</th>
                  <th style={{ padding: '14px', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Status</th>
                  <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Last Login</th>
                  <th style={{ padding: '14px', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '14px', fontWeight: '500' }}>{u.username}</td>
                    <td style={{ padding: '14px' }}>{u.full_name || '-'}</td>
                    <td style={{ padding: '14px', textAlign: 'center' }}>{getRoleBadge(u.role)}</td>
                    <td style={{ padding: '14px', textAlign: 'center' }}>{getStatusBadge(u.status)}</td>
                    <td style={{ padding: '14px', fontSize: '12px', color: '#6b7280' }}>{formatDate(u.last_login)}</td>
                    <td style={{ padding: '14px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button onClick={() => { setSelectedUser(u); setShowEditModal(true); }} style={{
                          padding: '6px 12px', border: 'none', borderRadius: '6px',
                          background: '#3b82f6', color: 'white', cursor: 'pointer', fontSize: '12px'
                        }}>Edit</button>
                        <button onClick={() => { setSelectedUser(u); setShowResetModal(true); }} style={{
                          padding: '6px 12px', border: 'none', borderRadius: '6px',
                          background: '#f59e0b', color: 'white', cursor: 'pointer', fontSize: '12px'
                        }}>Reset</button>
                        <button onClick={() => handleDeleteUser(u.id)} style={{
                          padding: '6px 12px', border: 'none', borderRadius: '6px',
                          background: '#ef4444', color: 'white', cursor: 'pointer', fontSize: '12px'
                        }}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Create New User</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateUser} style={{ padding: '20px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Username *</label>
                <input type="text" required value={newUser.username} onChange={e => setNewUser({...newUser, username: e.target.value})}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }} />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Password *</label>
                <input type="password" required value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }} />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Full Name</label>
                <input type="text" value={newUser.full_name} onChange={e => setNewUser({...newUser, full_name: e.target.value})}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }} />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Email</label>
                <input type="email" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }} />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Role *</label>
                <select value={newUser.role} onChange={e => setNewUser({...newUser, role: e.target.value})}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}>
                  <option value="user">👤 User - Can view and enter data</option>
                  <option value="viewer">👁️ Viewer - Can only view summary (read-only)</option>
                  <option value="admin">👑 Admin - Full access</option>
                </select>
                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px' }}>
                  {newUser.role === 'viewer' && '⚠️ Viewers can only see the daily production summary dashboard. No data entry access.'}
                  {newUser.role === 'user' && 'Users can enter and view data in all dashboards but cannot see financial details.'}
                  {newUser.role === 'admin' && 'Admins have full access including user management and financial data.'}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowCreateModal(false)} style={{
                  padding: '10px 20px', border: '1px solid #ddd', borderRadius: '6px', background: '#fff', cursor: 'pointer'
                }}>Cancel</button>
                <button type="submit" style={{
                  padding: '10px 20px', border: 'none', borderRadius: '6px',
                  background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', cursor: 'pointer', fontWeight: '600'
                }}>Create User</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Edit User: {selectedUser.username}</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <form onSubmit={handleUpdateUser} style={{ padding: '20px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Full Name</label>
                <input type="text" value={selectedUser.full_name || ''} onChange={e => setSelectedUser({...selectedUser, full_name: e.target.value})}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }} />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Email</label>
                <input type="email" value={selectedUser.email || ''} onChange={e => setSelectedUser({...selectedUser, email: e.target.value})}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }} />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Role</label>
                <select value={selectedUser.role} onChange={e => setSelectedUser({...selectedUser, role: e.target.value})}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}>
                  <option value="user">👤 User</option>
                  <option value="viewer">👁️ Viewer</option>
                  <option value="admin">👑 Admin</option>
                </select>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Status</label>
                <select value={selectedUser.status} onChange={e => setSelectedUser({...selectedUser, status: e.target.value})}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="locked">Locked</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowEditModal(false)} style={{
                  padding: '10px 20px', border: '1px solid #ddd', borderRadius: '6px', background: '#fff', cursor: 'pointer'
                }}>Cancel</button>
                <button type="submit" style={{
                  padding: '10px 20px', border: 'none', borderRadius: '6px',
                  background: 'linear-gradient(135deg, #3b82f6, #60a5fa)', color: 'white', cursor: 'pointer', fontWeight: '600'
                }}>Update User</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowResetModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h2 className="modal-title">Reset Password</h2>
              <button className="modal-close" onClick={() => setShowResetModal(false)}>×</button>
            </div>
            <form onSubmit={handleResetPassword} style={{ padding: '20px' }}>
              <p style={{ marginBottom: '16px', color: '#6b7280' }}>
                Reset password for user: <strong>{selectedUser.username}</strong>
              </p>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>New Password *</label>
                <input type="password" required minLength={6} value={newPassword} onChange={e => setNewPassword(e.target.value)}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }} />
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowResetModal(false)} style={{
                  padding: '10px 20px', border: '1px solid #ddd', borderRadius: '6px', background: '#fff', cursor: 'pointer'
                }}>Cancel</button>
                <button type="submit" style={{
                  padding: '10px 20px', border: 'none', borderRadius: '6px',
                  background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: 'white', cursor: 'pointer', fontWeight: '600'
                }}>Reset Password</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: '20px', right: '20px', padding: '12px 24px',
          background: toast.type === 'error' ? '#ef4444' : '#10b981', color: 'white',
          borderRadius: '8px', boxShadow: '0 4px 15px rgba(0,0,0,0.2)', zIndex: 1000
        }}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default UserManagement;